/**
 * SuggestionSetDetailsSheet - Full-screen modal for sync suggestion details
 *
 * Features:
 * - Side-by-side comparison of Finch and Filevine entities
 * - Highlights fields that have suggested changes
 * - Shows AI reasoning and confidence scores
 * - Reuses existing entity detail components
 */

'use client';

import { ArrowLeftRight, Building2, Clock, User } from 'lucide-react';
import React, { useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FinchEntity } from '@/services/api/entitySyncService';
import type {
  DetailedFilevineCollectionItemSchema,
  FilevineSectionFieldConfigSchema,
  FilevineSectionPayload,
} from '@/services/api/generated/entitySyncService';

import { CoverageEntityDetails } from '../../shared/CoverageEntityDetails';
import {
  FilevineEntityDetails,
  type FilevineSectionFieldValue,
} from '../../shared/FilevineEntityDetails';
import { MedicalProviderEntityDetails } from '../../shared/MedicalProviderEntityDetails';
import {
  isCoverageEntity,
  isMedicalProviderEntity,
} from '../../utils/typeGuards';
import { ConfidenceScore } from './ConfidenceScore';
import type { SuggestionSetDetailsSheetProps } from './types';

/**
 * Format a link type to a human-readable label
 */
function formatLinkType(linkType: string): string {
  return linkType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

/**
 * Format a date to a readable string
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get status badge variant
 */
function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'PENDING':
      return 'secondary';
    case 'ACCEPTED':
      return 'default';
    case 'REJECTED':
      return 'destructive';
    case 'SUPERSEDED':
      return 'outline';
    default:
      return 'outline';
  }
}

/**
 * Finch Entity Details Panel - Renders the appropriate entity details
 * component based on entity type
 */
const FinchEntityPanel: React.FC<{
  entity: FinchEntity | null | undefined;
  affectedFields: Set<string>;
  suggestedPayload?: Record<string, unknown>;
  editable?: boolean;
  onPayloadChange?: (
    fieldName: string,
    value: string | null,
  ) => void;
}> = ({ entity, affectedFields, suggestedPayload, editable, onPayloadChange }) => {
  // Handle no entity and no payload
  if (!entity && !suggestedPayload) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50 p-8">
        <p className="text-sm text-zinc-500">No Finch entity linked</p>
      </div>
    );
  }

  // Coverage entity
  if ((entity && isCoverageEntity(entity)) || (!entity && suggestedPayload && ('carrier_name' in suggestedPayload || 'policy_type' in suggestedPayload || 'category' in suggestedPayload))) {
    const coverageEntity = entity && isCoverageEntity(entity) ? entity : null;
    return (
      <CoverageEntityDetails
        entity={coverageEntity}
        affectedFields={affectedFields}
        suggestedPayload={suggestedPayload as Parameters<typeof CoverageEntityDetails>[0]['suggestedPayload']}
        editable={editable}
        onPayloadChange={onPayloadChange as Parameters<typeof CoverageEntityDetails>[0]['onPayloadChange']}
      />
    );
  }

  // Default: Medical provider entity
  const medicalProviderEntity =
    entity && isMedicalProviderEntity(entity) ? entity : null;

  return (
    <MedicalProviderEntityDetails
      entity={medicalProviderEntity}
      affectedFields={affectedFields}
      suggestedPayload={
        suggestedPayload as Parameters<typeof MedicalProviderEntityDetails>[0]['suggestedPayload']
      }
      editable={editable}
      onPayloadChange={onPayloadChange as Parameters<typeof MedicalProviderEntityDetails>[0]['onPayloadChange']}
    />
  );
};

/**
 * Filevine Entity Details Panel with inline change highlights
 */
const FilevineEntityPanel: React.FC<{
  entity: DetailedFilevineCollectionItemSchema | null | undefined;
  fieldConfigs: FilevineSectionFieldConfigSchema[];
  affectedFields: Set<string>;
  suggestedPayload?: FilevineSectionPayload;
  firmId?: string;
  editable?: boolean;
  onPayloadChange?: (
    fieldName: string,
    value: FilevineSectionFieldValue,
  ) => void;
}> = ({
  entity,
  fieldConfigs,
  affectedFields,
  suggestedPayload,
  firmId,
  editable,
  onPayloadChange,
}) => {
  // Show details even without entity - will display suggested values for CREATE

  const handlePayloadChange = (
    fieldName: string,
    value: FilevineSectionFieldValue,
  ) => {
    onPayloadChange?.(fieldName, value);
  };
  if (fieldConfigs.length > 0) {
    return (
      <FilevineEntityDetails
        item={entity ?? undefined}
        fieldConfigs={fieldConfigs}
        affectedFields={affectedFields}
        suggestedPayload={suggestedPayload}
        firmId={firmId}
        editable={editable}
        onPayloadChange={handlePayloadChange}
      />
    );
  }

  if (!entity) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50 p-8">
        <p className="text-sm text-zinc-500">No Filevine entity linked</p>
      </div>
    );
  }

  // Fallback: simple display
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <Building2 className="h-5 w-5 text-zinc-400" />
        <span className="font-medium text-zinc-900">
          Filevine Item #{entity.id.slice(0, 8)}
        </span>
      </div>
    </div>
  );
};

export const SuggestionSetDetailsSheet: React.FC<
  SuggestionSetDetailsSheetProps
> = ({
  suggestionSet,
  open,
  onOpenChange,
  finchEntity,
  filevineEntity,
  fieldConfigs = [],
  firmId,
  editable,
  onFilevinePayloadChange,
  modifiedFilevinePayload,
  finchEditable,
  onFinchPayloadChange,
  modifiedFinchPayload,
  direction,
}) => {
  // Filter out NO_CHANGE suggestions, and optionally filter by direction
  const suggestions = useMemo(
    () =>
      (suggestionSet?.suggestions ?? []).filter(
        (s) =>
          s.action_type !== 'NO_CHANGE' &&
          (!direction || s.direction === direction),
      ),
    [suggestionSet?.suggestions, direction],
  );

  // Compute affected fields for each direction
  const {
    finchAffectedFields,
    filevineAffectedFields,
    finchPayload,
    filevinePayload,
  } = useMemo(() => {
    const finchFields = new Set<string>();
    const filevineFields = new Set<string>();
    let finchPl: Record<string, unknown> | undefined;
    let filevinePl: FilevineSectionPayload | undefined;

    suggestions.forEach((s) => {
      const fields = s.affected_fields ?? [];
      const payload = s.modified_payload ?? s.suggested_payload;

      if (s.direction === 'TO_FINCH') {
        fields.forEach((f) => finchFields.add(f));
        finchPl = payload as Record<string, unknown>;
      } else {
        fields.forEach((f) => filevineFields.add(f));
        filevinePl = payload as FilevineSectionPayload | undefined;
      }
    });

    return {
      finchAffectedFields: finchFields,
      filevineAffectedFields: filevineFields,
      finchPayload: finchPl,
      filevinePayload: filevinePl,
    };
  }, [suggestions]);

  if (!suggestionSet) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] max-h-[90vh] w-[98vw] max-w-[1800px] flex-col overflow-hidden p-0">
        {/* Header */}
        <DialogHeader className="flex-shrink-0 border-b border-zinc-100 px-6 py-4">
          <div className="flex items-start justify-between pr-8">
            <div>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <ArrowLeftRight className="h-5 w-5 text-zinc-400" />
                Sync Comparison
              </DialogTitle>
              <DialogDescription className="mt-1">
                {formatLinkType(suggestionSet.link_type)} - Review suggested
                changes between systems
              </DialogDescription>
            </div>
            <ConfidenceScore
              confidence={suggestionSet.confidence ?? 0}
              size="lg"
              showLabel
            />
          </div>

          {/* Metadata */}
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-zinc-500">
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              <span>{formatDate(suggestionSet.created_at)}</span>
            </div>
            <Badge variant={getStatusVariant(suggestionSet.status)}>
              {suggestionSet.status.toLowerCase().replace(/_/g, ' ')}
            </Badge>
            {suggestions.length > 0 && (
              <Badge
                variant="outline"
                className="border-amber-300 bg-amber-50 text-amber-700"
              >
                {suggestions.length} change{suggestions.length !== 1 ? 's' : ''}{' '}
                suggested
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* Side-by-side comparison - full height with independent scrolling */}
        <div className="grid min-h-0 flex-1 grid-cols-2 gap-6 overflow-hidden p-6">
          {/* Finch Entity (Left) */}
          <div className="flex min-h-0 flex-col overflow-hidden">
            <div className="mb-4 flex flex-shrink-0 items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100">
                <User className="h-4 w-4 text-blue-600" />
              </div>
              <h3 className="font-semibold text-zinc-900">
                Finch {formatLinkType(suggestionSet.link_type)}
              </h3>
              {!finchEntity && finchPayload && (
                <Badge className="bg-emerald-600 text-white text-xs">
                  Will Create
                </Badge>
              )}
              {finchEntity && finchAffectedFields.size > 0 && (
                <Badge
                  variant="outline"
                  className="border-amber-300 bg-amber-50 text-amber-700 text-xs"
                >
                  {finchAffectedFields.size} field
                  {finchAffectedFields.size !== 1 ? 's' : ''} to update
                </Badge>
              )}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-zinc-200 bg-white p-4">
              <FinchEntityPanel
                entity={finchEntity}
                affectedFields={finchAffectedFields}
                suggestedPayload={modifiedFinchPayload ?? finchPayload}
                editable={finchEditable}
                onPayloadChange={onFinchPayloadChange}
              />
            </div>
          </div>

          {/* Filevine Entity (Right) */}
          <div className="flex min-h-0 flex-col overflow-hidden">
            <div className="mb-4 flex flex-shrink-0 items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100">
                <Building2 className="h-4 w-4 text-violet-600" />
              </div>
              <h3 className="font-semibold text-zinc-900">Filevine Item</h3>
              {!filevineEntity && filevinePayload && (
                <Badge className="bg-emerald-600 text-white text-xs">
                  Will Create
                </Badge>
              )}
              {filevineEntity && filevineAffectedFields.size > 0 && (
                <Badge
                  variant="outline"
                  className="border-amber-300 bg-amber-50 text-amber-700 text-xs"
                >
                  {filevineAffectedFields.size} field
                  {filevineAffectedFields.size !== 1 ? 's' : ''} to update
                </Badge>
              )}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-zinc-200 bg-white p-4">
              <FilevineEntityPanel
                entity={filevineEntity}
                fieldConfigs={fieldConfigs}
                affectedFields={filevineAffectedFields}
                suggestedPayload={modifiedFilevinePayload ?? filevinePayload}
                firmId={firmId}
                editable={editable}
                onPayloadChange={onFilevinePayloadChange}
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SuggestionSetDetailsSheet;
